import React, { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { CONTACT_EMAIL } from "../lib/contact";
import {
  ShieldCheck, ArrowRight, Lock, HeartPulse, GraduationCap,
  CalendarClock, AlertOctagon, ShieldAlert, CheckCircle2,
  TrendingDown, TrendingUp, Quote, Phone, Mail, Calendar,
  Brain, Target, Layers, GitBranch, Gauge, FileCheck,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { trackEvent } from "../lib/analytics";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
// Trial/Signin go through the Haruki-branded intake screen at /trial and /signin.
const TRIAL_PATH = "/trial";
const SIGNIN_PATH = "/signin";

// Animated counter — counts from 0 to target on viewport entry
const useCountUp = (target, durationMs = 1400) => {
  const [v, setV] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    if (!ref.current) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !started.current) {
        started.current = true;
        const start = performance.now();
        const step = (t) => {
          const p = Math.min(1, (t - start) / durationMs);
          setV(Math.round(target * (1 - Math.pow(1 - p, 3))));
          if (p < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      }
    }, { threshold: 0.4 });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [target, durationMs]);

  return [v, ref];
};

const HERO_STATS = [
  { value: 26, label: "Reduction in burnout", suffix: "%", down: true },
  { value: 50, label: "Reduction in divorce rates", suffix: "%", down: true },
  { value: 86, label: "Relationship satisfaction", suffix: "%", down: false },
  { value: 60, label: "Emotional intelligence", suffix: "%", down: false },
];

const FEATURES = [
  {
    icon: GraduationCap,
    tag: "Module 1",
    title: "New Hire Onboarding",
    desc: "Five foundational scenarios mapped to your vertical's core incident categories. Built into onboarding from day one. CEU certificate on completion.",
  },
  {
    icon: CalendarClock,
    tag: "Module 2",
    title: "Annual Resilience Training",
    desc: "Auto-scheduled 30 days before in-service anniversary. New scenarios every year, 12-month category exclusion to prevent re-exposure. Counts toward POST / accreditation.",
  },
  {
    icon: AlertOctagon,
    tag: "Module 3",
    title: "Critical Incident Response",
    desc: "Pushed by command in the hours after a critical event. Structured, scalable wrap-around to peer debriefs. Re-traumatization risk checked before delivery.",
  },
  {
    icon: ShieldAlert,
    tag: "Module 4",
    title: "SOS — Always Available",
    desc: "Persistent button on every screen. Routes to confidential clinician-grade session. Your agency gets a silent signal — never the content.",
  },
];

const OUTCOMES_DOWN = [
  { pct: 26, label: "Professional Burnout" },
  { pct: 50, label: "Divorce Rates" },
  { pct: 21, label: "Domestic Violence Incidents" },
  { pct: 30, label: "Substance Misuse & Abuse" },
];

const OUTCOMES_UP = [
  { pct: 60, label: "Emotional & Relational Intelligence" },
  { pct: 58, label: "Mental Wellness & Resilience Metrics" },
  { pct: 86, label: "Relationship Satisfaction" },
  { pct: 25, label: "Work Performance Evaluations" },
];

const TRUST_BADGES = [
  "MITRE-Licensed Framework",
  "POST Accreditation",
  "Enterprise-Grade Encryption",
  "Clinically Reviewed",
  "Agency-Owned Delivery",
  "HIPAA-Aligned",
];

export default function Landing() {
  const nav = useNavigate();
  const goTrial = (source) => { trackEvent("trial_click", { source }); nav(TRIAL_PATH); };
  const goSignin = () => { trackEvent("signin_click", { source: "landing_header" }); nav(SIGNIN_PATH); };
  const trackNav = (target) => trackEvent("nav_click", { target });
  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Sticky nav with conversion CTA */}
      <header className="sticky top-0 z-40 border-b border-border bg-background/85 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <a href="#top" className="flex items-center gap-2">
            <div className="rounded-md p-1.5" style={{ background: "hsl(var(--haruki-leaf))" }}>
              <ShieldCheck className="h-4 w-4 text-white" />
            </div>
            <span className="font-heading text-base font-bold tracking-tight">Haruki Health</span>
          </a>
          <nav className="hidden items-center gap-7 text-sm font-medium md:flex">
            <a href="#how" data-testid="nav-how" onClick={() => trackNav("how")} className="text-foreground/70 hover:text-foreground">How it works</a>
            <a href="#hmde" data-testid="nav-hmde" onClick={() => trackNav("hmde")} className="text-foreground/70 hover:text-foreground">HMDE engine</a>
            <a href="#modules" data-testid="nav-modules-section" onClick={() => trackNav("modules-section")} className="text-foreground/70 hover:text-foreground">Four modules</a>
            <a href="#outcomes" data-testid="nav-outcomes" onClick={() => trackNav("outcomes")} className="text-foreground/70 hover:text-foreground">Outcomes</a>
            <a href="#clinical" data-testid="nav-clinical" onClick={() => trackNav("clinical")} className="text-foreground/70 hover:text-foreground">Clinical foundation</a>
            <Link to="/speaking" data-testid="nav-speaking" onClick={() => trackNav("speaking")} className="text-foreground/70 hover:text-foreground">Speaking</Link>
          </nav>
          <div className="flex items-center gap-2">
            <Button
              data-testid="landing-signin-button"
              onClick={goSignin}
              variant="ghost"
              className="hidden text-sm sm:inline-flex"
            >
              Sign in
            </Button>
            <Button
              data-testid="landing-trial-nav-button"
              onClick={() => goTrial("landing_header")}
              className="text-sm font-semibold"
              style={{ background: "hsl(var(--haruki-leaf))", color: "white" }}
            >
              Start 21-day trial
            </Button>
          </div>
        </div>
      </header>

      <main id="top">
        {/* HERO */}
        <section className="haruki-grain border-b border-border">
          <div className="mx-auto grid max-w-6xl items-center gap-12 px-6 py-16 lg:grid-cols-[1.15fr_1fr] lg:py-24">
            <div>
              <div
                data-testid="trial-pill"
                className="mb-6 inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-bold uppercase tracking-[0.18em]"
                style={{ borderColor: "hsl(var(--haruki-leaf))", color: "hsl(var(--haruki-leaf-deep))", background: "hsl(var(--haruki-leaf) / 0.08)" }}
              >
                <CheckCircle2 className="h-3.5 w-3.5" style={{ color: "hsl(var(--haruki-leaf))" }} />
                21-day free trial · No card required
              </div>

              <h1 className="font-heading text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
                We strengthen the <span style={{ color: "hsl(var(--haruki-leaf))" }}>human</span> in the uniform.
              </h1>

              <div className="mt-6 space-y-1 text-base italic text-muted-foreground">
                <div>Haruki is not therapy. Haruki is not a wellness app.</div>
                <div className="font-medium not-italic text-foreground">It is tactical resilience training.</div>
              </div>

              <p className="mt-6 max-w-xl text-base leading-relaxed text-muted-foreground">
                Built into the structure of the agency itself — through onboarding, annual training, and critical
                incident response. The agency deploys it. The calendar holds it. The standard demands it.
              </p>

              <div className="mt-8 flex flex-wrap items-center gap-4">
                <Button
                  data-testid="landing-get-started-button"
                  onClick={() => goTrial("landing_hero")}
                  className="text-base font-semibold"
                  size="lg"
                  style={{ background: "hsl(var(--haruki-leaf))", color: "white" }}
                >
                  Start 21-day free trial <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <a
                  href="/demo"
                  data-testid="landing-try-scenario-button"
                  className="inline-flex items-center gap-2 rounded-md border-2 px-5 py-3 text-sm font-semibold transition-colors hover:bg-card"
                  style={{ borderColor: "hsl(var(--haruki-leaf))", color: "hsl(var(--haruki-leaf-deep))" }}
                >
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-75" style={{ background: "hsl(var(--haruki-leaf))" }} />
                    <span className="relative inline-flex h-2 w-2 rounded-full" style={{ background: "hsl(var(--haruki-leaf))" }} />
                  </span>
                  Try a scenario · 60 seconds
                </a>
              </div>

              <div className="mt-2 text-xs text-muted-foreground">
                No sign-up needed for the demo. No card needed for the trial.
              </div>

              <div className="mt-10 flex flex-wrap gap-x-6 gap-y-2 text-xs text-muted-foreground">
                {TRUST_BADGES.slice(0, 4).map(t => (
                  <span key={t} className="inline-flex items-center gap-1">
                    <CheckCircle2 className="h-3 w-3" style={{ color: "hsl(var(--haruki-leaf))" }} />
                    {t}
                  </span>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="absolute -left-4 -top-4 h-24 w-24 rounded-full opacity-20"
                   style={{ background: "hsl(var(--haruki-leaf))" }} />
              <div className="absolute -bottom-6 -right-2 h-32 w-32 rounded-full opacity-10"
                   style={{ background: "hsl(var(--haruki-rust))" }} />
              <div
                className="relative rounded-2xl border border-border p-6 shadow-sm lg:p-8"
                style={{ background: "hsl(var(--card))" }}
              >
                <div className="mb-3 flex items-center justify-between">
                  <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
                    Scenario · Law Enforcement
                  </span>
                  <span className="rounded-full px-2 py-0.5 text-[10px] font-semibold"
                        style={{ background: "hsl(var(--haruki-leaf) / 0.15)", color: "hsl(var(--haruki-leaf-deep))" }}>
                    Reflective modality
                  </span>
                </div>
                <p className="text-sm leading-relaxed">
                  You and your partner have just cleared a domestic violence call. The kids are safe with their aunt.
                  Back at the patrol vehicle, your partner is quieter than usual, staring out the windshield.
                  The shift isn't over, but the call left a residue you both feel.
                </p>
                <div className="mt-5 space-y-2">
                  {["Acknowledge what happened and ask how they are.",
                    "Step outside, take three slow breaths, and come back.",
                    "Refocus on the next task — finish the paperwork together.",
                    "Tell them one thing you noticed during the call."].map((t, i) => (
                    <div key={i}
                         className="rounded-md border border-border bg-secondary/40 px-3 py-2 text-xs transition-colors hover:bg-secondary">
                      {t}
                    </div>
                  ))}
                </div>
                <div className="mt-5 flex items-center justify-between border-t border-border pt-4 text-xs text-muted-foreground">
                  <span>Avatar: Partner Officer</span>
                  <span className="inline-flex items-center gap-1">
                    <Lock className="h-3 w-3" /> Zone label only · scores hidden
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Hero stat strip */}
          <div className="border-t border-border" style={{ background: "hsl(var(--haruki-leaf-deep))" }}>
            <div className="mx-auto grid max-w-6xl grid-cols-2 gap-6 px-6 py-10 text-white lg:grid-cols-4">
              {HERO_STATS.map((s) => (
                <HeroStat key={s.label} stat={s} />
              ))}
            </div>
          </div>
        </section>

        {/* WHAT HARUKI IS / IS NOT */}
        <section id="how" className="border-b border-border">
          <div className="mx-auto max-w-6xl px-6 py-20 lg:py-28">
            <div className="grid gap-12 lg:grid-cols-2">
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">
                  How it works
                </div>
                <h2 className="mt-3 font-heading text-3xl font-bold sm:text-4xl">
                  Build the muscle <span style={{ color: "hsl(var(--haruki-leaf))" }}>before</span> the call.
                </h2>
                <p className="mt-5 text-base leading-relaxed text-muted-foreground">
                  The same way you train to clear a building, establish an IV line, or memorize every street in your
                  district, resilience requires repetition, realism, and practice before the moment demands it.
                </p>
                <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                  Through scenario-based training drawn from real occupational experiences — the calls that stay with
                  you, the shifts that follow you home, and challenges of life outside the job — personnel develop
                  psychological muscle memory to recognize stress early, regulate effectively, and recover faster.
                </p>
              </div>

              <div className="space-y-3">
                {[
                  { label: "Resilience", desc: "Develop psychological muscle memory for high-stress situations. Before the call. Before the crisis." },
                  { label: "Regulation", desc: "Evidence-based techniques to manage stress responses, process critical incidents, and maintain operational effectiveness." },
                  { label: "Recovery", desc: "Targeted training deployed after critical incidents — a structured, scalable wrap-around to debriefs, on any device." },
                ].map((step, i) => (
                  <div key={step.label}
                       className="flex items-start gap-4 rounded-lg border border-border p-5 transition-shadow hover:shadow-sm"
                       style={{ background: "hsl(var(--card))" }}>
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full font-heading text-sm font-bold"
                         style={{ background: "hsl(var(--haruki-leaf))", color: "white" }}>
                      {i + 1}
                    </div>
                    <div>
                      <div className="font-heading text-base font-bold">{step.label}</div>
                      <div className="mt-1 text-sm leading-relaxed text-muted-foreground">{step.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* HMDE EXPLAINER */}
        <section id="hmde" className="border-b border-border" style={{ background: "hsl(var(--background))" }}>
          <div className="mx-auto max-w-6xl px-6 py-20 lg:py-28">
            <div className="mx-auto max-w-2xl text-center">
              <div className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: "hsl(var(--haruki-leaf-deep))" }}>
                The clinical engine
              </div>
              <h2 className="mt-3 font-heading text-3xl font-bold sm:text-4xl">
                Meet HMDE — the engine behind every scenario.
              </h2>
              <p className="mt-4 text-base text-muted-foreground">
                The <strong>Haruki Modality Detection Engine (HMDE)</strong> is a dual-pass clinical scoring
                engine. It evaluates every scenario before it reaches a service member, then evaluates each
                response across six clinical dimensions — without ever exposing a number.
              </p>
            </div>

            {/* Two-call flow */}
            <div className="mt-14 grid gap-6 lg:grid-cols-2">
              <div className="rounded-xl border border-border p-7" style={{ background: "hsl(var(--card))" }}>
                <div className="flex items-center gap-3">
                  <div className="rounded-md p-2.5" style={{ background: "hsl(var(--haruki-leaf) / 0.12)" }}>
                    <FileCheck className="h-5 w-5" style={{ color: "hsl(var(--haruki-leaf-deep))" }} />
                  </div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">HMDE · Call 1</div>
                </div>
                <h3 className="mt-3 font-heading text-lg font-bold">Content scoring · before delivery</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  Every scenario — whether from the library or freshly generated — is scored for clinical
                  appropriateness before it reaches a service member. A minimum score of 75 is required to publish.
                  Clinical flags trigger automatic rejection and a regeneration pass (up to 3 times) before queueing
                  for manual clinical review.
                </p>
                <ul className="mt-4 space-y-1.5 text-xs text-muted-foreground">
                  {["Re-traumatization risk check (critical incidents)",
                    "Modality alignment with target vertical",
                    "Clinical-flag scan: explicit detail, identifying detail, graphic content"].map(b => (
                    <li key={b} className="flex items-start gap-2">
                      <CheckCircle2 className="mt-0.5 h-3 w-3 shrink-0" style={{ color: "hsl(var(--haruki-leaf))" }} />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-xl border border-border p-7" style={{ background: "hsl(var(--card))" }}>
                <div className="flex items-center gap-3">
                  <div className="rounded-md p-2.5" style={{ background: "hsl(var(--haruki-leaf) / 0.12)" }}>
                    <Gauge className="h-5 w-5" style={{ color: "hsl(var(--haruki-leaf-deep))" }} />
                  </div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">HMDE · Call 2</div>
                </div>
                <h3 className="mt-3 font-heading text-lg font-bold">Response scoring · after the response</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  After a service member responds (by choice or in their own words via voice or text), HMDE
                  scores their response across <strong>six clinical dimensions</strong>, classifies it into one of
                  three zones, and generates a calibrated reflection prompt. The service member sees the zone
                  label only — never the score.
                </p>
                <ul className="mt-4 space-y-1.5 text-xs text-muted-foreground">
                  {["Six weighted clinical dimensions",
                    "Modality detection + next-modality recommendation",
                    "Personalized reflection prompt"].map(b => (
                    <li key={b} className="flex items-start gap-2">
                      <CheckCircle2 className="mt-0.5 h-3 w-3 shrink-0" style={{ color: "hsl(var(--haruki-leaf))" }} />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Six dimensions */}
            <div className="mt-14">
              <div className="mb-6 text-xs font-bold uppercase tracking-[0.2em]" style={{ color: "hsl(var(--haruki-leaf-deep))" }}>
                Six dimensions · clinically weighted
              </div>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {[
                  { icon: Target, name: "Conceptual match", weight: 30, blurb: "How well the response engages the clinical concept the scenario was designed to surface." },
                  { icon: Brain, name: "Linguistic markers", weight: 20, blurb: "Word choice, syntax, hedging language — markers of regulation vs. activation." },
                  { icon: HeartPulse, name: "Emotional tone fit", weight: 15, blurb: "Whether emotional tone is calibrated to the moment, not over- or under-tuned." },
                  { icon: GitBranch, name: "Interaction type", weight: 10, blurb: "Action-oriented, reflective, directive, supportive, narrative — and whether the choice fits." },
                  { icon: Layers, name: "Narrative coherence", weight: 10, blurb: "Internal consistency and meaning-making within the response itself." },
                  { icon: TrendingUp, name: "Past response behavior", weight: 15, blurb: "Pattern recognition against the member's own training history — adaptive, never comparative." },
                ].map((d, i) => (
                  <div key={d.name}
                       data-testid={`landing-hmde-dim-${i}`}
                       className="rounded-lg border border-border p-5"
                       style={{ background: "hsl(var(--card))" }}>
                    <div className="flex items-start gap-3">
                      <d.icon className="mt-0.5 h-4 w-4 shrink-0" style={{ color: "hsl(var(--haruki-leaf-deep))" }} />
                      <div className="flex-1">
                        <div className="flex items-baseline gap-2">
                          <div className="font-heading text-sm font-bold">{d.name}</div>
                          <div className="text-xs font-bold tabular-nums" style={{ color: "hsl(var(--haruki-leaf))" }}>{d.weight}%</div>
                        </div>
                        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{d.blurb}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Three zones */}
            <div className="mt-14">
              <div className="mb-6 text-xs font-bold uppercase tracking-[0.2em]" style={{ color: "hsl(var(--haruki-leaf-deep))" }}>
                Three zones · labels only, never numbers
              </div>
              <div className="grid gap-4 sm:grid-cols-3">
                {[
                  { zone: "Resilience Zone", color: "hsl(var(--haruki-leaf))", blurb: "Grounded awareness. Regulated attention. The response Haruki training expands access to." },
                  { zone: "Neutral Zone", color: "hsl(var(--haruki-neutral))", blurb: "Functional, steady. The default mode for many responders — productive, but with room to grow." },
                  { zone: "Emotional Fire Zone", color: "hsl(var(--haruki-fire))", blurb: "Elevated emotional charge. Normal after high-stress calls — and exactly what HMDE is built to help regulate." },
                ].map(z => (
                  <div key={z.zone}
                       data-testid={`landing-hmde-zone-${z.zone}`}
                       className="rounded-lg border-2 p-5"
                       style={{ borderColor: z.color, background: "hsl(var(--card))" }}>
                    <div className="font-heading text-base font-extrabold" style={{ color: z.color }}>
                      {z.zone}
                    </div>
                    <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{z.blurb}</p>
                  </div>
                ))}
              </div>
              <div className="mt-6 inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-xs"
                   style={{ background: "hsl(var(--secondary))" }}>
                <Lock className="h-3 w-3" style={{ color: "hsl(var(--haruki-leaf-deep))" }} />
                <span>
                  <strong>Confidentiality boundary:</strong> service members see zone labels only.
                  Numeric scores never appear on any screen, ever — by design and by database policy.
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* FOUR MODULES */}
        <section id="modules" className="border-b border-border" style={{ background: "hsl(var(--secondary))" }}>
          <div className="mx-auto max-w-6xl px-6 py-20 lg:py-28">
            <div className="mx-auto max-w-2xl text-center">
              <div className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: "hsl(var(--haruki-leaf-deep))" }}>
                Four training modules · One platform
              </div>
              <h2 className="mt-3 font-heading text-3xl font-bold sm:text-4xl">
                Everything a resilience program needs — built in.
              </h2>
              <p className="mt-4 text-base text-muted-foreground">
                Each module delivered through a library-first architecture and dual HMDE clinical scoring engine.
                Your 21-day trial includes full access to all four.
              </p>
            </div>

            <div className="mt-14 grid gap-6 sm:grid-cols-2">
              {FEATURES.map((f, i) => (
                <div
                  key={f.title}
                  data-testid={`landing-feature-${i}`}
                  className="group rounded-xl border border-border p-7 transition-all hover:border-foreground/20 hover:shadow-md"
                  style={{ background: "hsl(var(--card))" }}
                >
                  <div className="flex items-start gap-5">
                    <div className="rounded-lg p-3 transition-transform group-hover:scale-105"
                         style={{ background: "hsl(var(--haruki-leaf) / 0.12)" }}>
                      <f.icon className="h-6 w-6" style={{ color: "hsl(var(--haruki-leaf-deep))" }} />
                    </div>
                    <div className="flex-1">
                      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">{f.tag}</div>
                      <div className="mt-1 font-heading text-lg font-bold">{f.title}</div>
                      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* OUTCOMES */}
        <section id="outcomes" className="border-b border-border">
          <div className="mx-auto max-w-6xl px-6 py-20 lg:py-28">
            <div className="mx-auto max-w-2xl text-center">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">
                Proven & measurable impact
              </div>
              <h2 className="mt-3 font-heading text-3xl font-bold sm:text-4xl">
                Proven results for those on the <span style={{ color: "hsl(var(--haruki-leaf))" }}>frontlines</span>.
              </h2>
              <p className="mt-4 text-base text-muted-foreground">
                Haruki closes the gap between the professional who shows up ready for the job — and the person who
                goes home whole.
              </p>
            </div>

            <div className="mt-14 grid gap-8 lg:grid-cols-2">
              <OutcomeCard
                title="Reducing negative outcomes"
                icon={TrendingDown}
                color="hsl(var(--haruki-rust))"
                items={OUTCOMES_DOWN}
                direction="down"
              />
              <OutcomeCard
                title="Enhancing performance & wellbeing"
                icon={TrendingUp}
                color="hsl(var(--haruki-leaf))"
                items={OUTCOMES_UP}
                direction="up"
              />
            </div>
          </div>
        </section>

        {/* CLINICAL FOUNDATION */}
        <section id="clinical" className="border-b border-border" style={{ background: "hsl(var(--haruki-leaf-deep))" }}>
          <div className="mx-auto max-w-6xl px-6 py-20 lg:py-28">
            <div className="grid gap-12 text-white lg:grid-cols-[1.3fr_1fr]">
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: "hsl(var(--haruki-leaf))" }}>
                  The clinical foundation
                </div>
                <h2 className="mt-3 font-heading text-3xl font-bold sm:text-4xl">
                  Built by a 25-year clinician who saw the same pattern, every time.
                </h2>
                <p className="mt-5 text-base leading-relaxed text-white/80">
                  Dr. Jeannelle Perkins spent more than 25 years treating military personnel, veterans, and first
                  responders across military installations, VA settings, and private practice. For two decades she
                  watched the same pattern repeat: professionals entered high-stress occupations with no proactive
                  resilience training, received little support until something broke, and by the time they sought
                  help, the psychological injury had already moved into their families and their lives.
                </p>
                <p className="mt-4 text-base leading-relaxed text-white/80">
                  She did not build Haruki to respond to that pattern. She built it to <em>interrupt</em> it.
                </p>
                <div className="mt-8 rounded-lg border border-white/15 p-6">
                  <Quote className="h-6 w-6" style={{ color: "hsl(var(--haruki-leaf))" }} />
                  <blockquote className="mt-3 font-heading text-lg italic leading-relaxed">
                    "Twenty-five years in the room with the people this nation depends on taught me one thing: they
                    were never given the tools before the weight became unbearable. Haruki is the tool I wish I could
                    have given every one of them, before the call that broke them, not after."
                  </blockquote>
                  <div className="mt-4 text-sm font-semibold">— Dr. Jeannelle Perkins, LMFT-S, EMDR</div>
                  <div className="text-xs text-white/60">Founder · Master Resilience Trainer (U.S. DoD)</div>
                </div>
              </div>

              <div className="space-y-3">
                <div className="rounded-lg border border-white/15 p-5">
                  <div className="text-xs font-semibold uppercase tracking-widest" style={{ color: "hsl(var(--haruki-leaf))" }}>
                    Framework
                  </div>
                  <div className="mt-2 font-heading text-base font-bold">MITRE-Licensed</div>
                  <p className="mt-1 text-sm text-white/70">Combined with two decades of academic and field research. A clinical depth no external vendor can replicate.</p>
                </div>
                <div className="rounded-lg border border-white/15 p-5">
                  <div className="text-xs font-semibold uppercase tracking-widest" style={{ color: "hsl(var(--haruki-leaf))" }}>
                    Credentials
                  </div>
                  <div className="mt-2 font-heading text-base font-bold">LMFT-S, EMDR</div>
                  <p className="mt-1 text-sm text-white/70">Counselor Education & Supervision · Marriage & Family Therapy · 22 countries delivering training.</p>
                </div>
                <div className="rounded-lg border border-white/15 p-5">
                  <div className="text-xs font-semibold uppercase tracking-widest" style={{ color: "hsl(var(--haruki-leaf))" }}>
                    Governance
                  </div>
                  <div className="mt-2 font-heading text-base font-bold">Every Scenario Reviewed</div>
                  <p className="mt-1 text-sm text-white/70">Dual HMDE clinical scoring engine. Clinician approval required before publish.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* WHY AGENCIES CHOOSE HARUKI */}
        <section className="border-b border-border">
          <div className="mx-auto max-w-6xl px-6 py-20 lg:py-28">
            <div className="mx-auto max-w-2xl text-center">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">
                Why agencies choose Haruki
              </div>
              <h2 className="mt-3 font-heading text-3xl font-bold sm:text-4xl">
                Owned by the agency. Trusted by the people.
              </h2>
            </div>
            <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {[
                { t: "Ease of Delivery", d: "Secure mobile + desktop. Owned and delivered by the agency to ensure compliance." },
                { t: "Seamless Integration", d: "Integrates directly with existing public-safety infrastructure for immediate access during critical incidents." },
                { t: "Completion Certificates", d: "CEUs usable for POST certification, accreditation, and compliance — issued per personnel, per module." },
                { t: "Scenario-Based Training", d: "Immersive branching-logic simulations based on real critical incidents and post-mission debriefs." },
                { t: "Agency Dashboards", d: "Cohort resilience reports with completion indicators — full insight for leadership and HR." },
                { t: "Uncompromising Privacy", d: "Enterprise-grade security. Confidentiality boundaries enforced at the database level." },
              ].map((x, i) => (
                <div key={x.t}
                     data-testid={`landing-why-${i}`}
                     className="rounded-lg border border-border p-6 transition-shadow hover:shadow-sm"
                     style={{ background: "hsl(var(--card))" }}>
                  <CheckCircle2 className="h-5 w-5" style={{ color: "hsl(var(--haruki-leaf))" }} />
                  <div className="mt-3 font-heading text-base font-bold">{x.t}</div>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{x.d}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="border-b border-border" style={{ background: "hsl(var(--secondary))" }}>
          <div className="mx-auto max-w-4xl px-6 py-20 text-center lg:py-28">
            <div className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: "hsl(var(--haruki-leaf-deep))" }}>
              21 days · No cost · No credit card
            </div>
            <h2 className="mt-3 font-heading text-3xl font-bold sm:text-4xl lg:text-5xl">
              Make resilience part of your training calendar.
            </h2>
            <p className="mt-5 mx-auto max-w-xl text-base text-muted-foreground">
              Join the growing number of agencies seeing significant improvements in mental health outcomes,
              relationship satisfaction, and professional performance among their teams.
            </p>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
              <Button
                data-testid="landing-trial-cta-button"
                onClick={() => goTrial("landing_footer_cta")}
                className="text-base font-semibold"
                size="lg"
                style={{ background: "hsl(var(--haruki-leaf))", color: "white" }}
              >
                Begin 21-day trial <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                data-testid="landing-contact-link"
                onClick={() => trackEvent("talk_to_team_click", { source: "landing_footer_cta" })}
                className="inline-flex items-center gap-2 rounded-md border border-border px-5 py-3 text-sm font-semibold transition-colors hover:bg-card"
              >
                <Mail className="h-4 w-4" /> Talk to our team
              </a>
            </div>

            <div className="mt-10 grid gap-4 text-sm text-muted-foreground sm:grid-cols-3">
              <a href="tel:843-730-2617" className="inline-flex items-center justify-center gap-2 hover:text-foreground">
                <Phone className="h-4 w-4" /> (843) 730-2617
              </a>
              <a href={`mailto:${CONTACT_EMAIL}`} className="inline-flex items-center justify-center gap-2 hover:text-foreground">
                <Mail className="h-4 w-4" /> {CONTACT_EMAIL}
              </a>
              <a href="https://calendly.com/harukihealth-support" target="_blank" rel="noreferrer"
                 className="inline-flex items-center justify-center gap-2 hover:text-foreground">
                <Calendar className="h-4 w-4" /> Book a 30-day demo
              </a>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-border bg-background py-10">
        <div className="mx-auto max-w-6xl px-6 text-center">
          <div className="flex items-center justify-center gap-2">
            <div className="rounded-md p-1.5" style={{ background: "hsl(var(--haruki-leaf))" }}>
              <ShieldCheck className="h-4 w-4 text-white" />
            </div>
            <span className="font-heading text-base font-bold">Haruki Health</span>
          </div>
          <div className="mt-3 text-xs text-muted-foreground">
            Tactical resilience training · Owned by the agency · Built for the people who run toward the call.
          </div>
        </div>
      </footer>
    </div>
  );
}

// ---- Subcomponents ----
const HeroStat = ({ stat }) => {
  const [v, ref] = useCountUp(stat.value);
  return (
    <div ref={ref} className="text-center">
      <div className="font-heading text-4xl font-extrabold tracking-tight sm:text-5xl">
        {stat.down ? <span style={{ color: "hsl(var(--haruki-leaf))" }}>↓</span> : <span style={{ color: "hsl(var(--haruki-leaf))" }}>↑</span>}
        {" "}{v}{stat.suffix}
      </div>
      <div className="mt-2 text-xs uppercase tracking-widest text-white/70">{stat.label}</div>
    </div>
  );
};

const OutcomeCard = ({ title, icon: Icon, color, items, direction }) => (
  <div className="rounded-xl border border-border p-7" style={{ background: "hsl(var(--card))" }}>
    <div className="flex items-center gap-3">
      <div className="rounded-md p-2" style={{ background: `${color} / 0.15`, backgroundColor: color, color: "white" }}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="font-heading text-base font-bold">{title}</div>
    </div>
    <div className="mt-6 space-y-4">
      {items.map((it, i) => (
        <OutcomeRow key={it.label} item={it} color={color} index={i} direction={direction} />
      ))}
    </div>
  </div>
);

const OutcomeRow = ({ item, color, index, direction }) => {
  const [v, ref] = useCountUp(item.pct, 1200);
  return (
    <div ref={ref} className="flex items-center gap-4">
      <div className="w-20 shrink-0 font-heading text-2xl font-extrabold tabular-nums" style={{ color }}>
        {direction === "down" ? "↓" : "↑"} {v}%
      </div>
      <div className="flex-1">
        <div className="text-sm font-semibold">{item.label}</div>
        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full" style={{ background: "hsl(var(--secondary))" }}>
          <div className="h-full rounded-full transition-all" style={{ width: `${v}%`, background: color, transitionDuration: "1200ms" }} />
        </div>
      </div>
    </div>
  );
};
